// Detail panel under the title of the transaction-status popup: one row per
// fact the flow actually has data for.
//
// Rows are nullable ON PURPOSE. A flow that cannot supply a field passes `null`
// and the panel renders one row fewer, rather than an em-dash placeholder that
// looks like a loading state that never resolves. When every row is null the
// panel does not render at all.
import React, { FC, ReactNode, useMemo } from 'react'
import { View } from 'react-native'

import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

export type StatusPanelRow = {
  /** Already translated — the panel never calls `t()` itself. */
  label: string
  /** A string renders as the standard right-aligned value; anything else is rendered as-is. */
  value: ReactNode
}

type Props = {
  rows: (StatusPanelRow | null | false | undefined)[]
}

const StatusPanel: FC<Props> = ({ rows }) => {
  const { theme } = useTheme()

  const visibleRows = useMemo(
    () => rows.filter((row): row is StatusPanelRow => !!row && row.value !== null),
    [rows]
  )

  if (!visibleRows.length) return null

  return (
    <View
      style={{
        width: '100%',
        borderRadius: 12,
        borderWidth: 1,
        backgroundColor: theme.controlSurfaceBackground,
        borderColor: theme.controlSurfaceBorder,
        paddingHorizontal: 16
      }}
    >
      {visibleRows.map((row, index) => (
        <View
          key={row.label}
          style={[
            flexbox.directionRow,
            flexbox.alignCenter,
            flexbox.justifySpaceBetween,
            {
              paddingVertical: 12,
              borderTopWidth: index === 0 ? 0 : 1,
              borderTopColor: theme.settingsDivider
            }
          ]}
        >
          <Text
            fontSize={13}
            appearance="secondaryText"
            numberOfLines={2}
            // A long label wraps instead of squeezing the value into "0.25 US…".
            style={{ flexShrink: 1, maxWidth: '55%' }}
          >
            {row.label}
          </Text>
          <View
            style={[
              flexbox.directionRow,
              flexbox.alignCenter,
              flexbox.justifyEnd,
              { flex: 1, minWidth: 0, marginLeft: 12 }
            ]}
          >
            {typeof row.value === 'string' || typeof row.value === 'number' ? (
              <Text
                fontSize={13}
                weight="medium"
                appearance="primaryText"
                numberOfLines={1}
                style={text.right}
              >
                {row.value}
              </Text>
            ) : (
              row.value
            )}
          </View>
        </View>
      ))}
    </View>
  )
}

export default React.memo(StatusPanel)
